/**
 * Maps domain states onto the semantic colour ramp.
 *
 * Centralised deliberately: if every screen chose its own badge colour, a
 * status would eventually mean amber in one place and red in another, and the
 * overdue-loans screen is precisely where that ambiguity costs money.
 *
 * `primary` (brand maroon) never appears here. It means "UNIFEMGA", not
 * "something needs attention".
 */

import type {
  DisbursementStatus,
  GuarantorStatus,
  InstallmentStatus,
  KycStatus,
  LoanApplicationStatus,
  LoanStatus,
  MemberStatus,
  NotificationStatus,
} from '@unifemga/shared';

export type StatusTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

export interface StatusPresentation {
  label: string;
  tone: StatusTone;
  /** Longer explanation for tooltips and empty states. */
  description?: string;
}

const TONE_CLASSES: Record<StatusTone, string> = {
  success: 'bg-success-soft text-success-soft-foreground ring-success/20',
  warning: 'bg-warning-soft text-warning-soft-foreground ring-warning/20',
  danger: 'bg-danger-soft text-danger-soft-foreground ring-danger/20',
  info: 'bg-info-soft text-info-soft-foreground ring-info/20',
  neutral: 'bg-neutral-soft text-neutral-soft-foreground ring-neutral/20',
};

const TONE_DOT_CLASSES: Record<StatusTone, string> = {
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
  info: 'bg-info',
  neutral: 'bg-neutral',
};

export const toneClasses = (tone: StatusTone): string => TONE_CLASSES[tone];
export const toneDotClasses = (tone: StatusTone): string => TONE_DOT_CLASSES[tone];

export const LOAN_STATUS: Record<LoanStatus, StatusPresentation> = {
  PENDING_DISBURSEMENT: {
    label: 'Awaiting disbursement',
    tone: 'info',
    description: 'Approved. Finance has not yet released the funds.',
  },
  DISBURSED: { label: 'Disbursed', tone: 'info', description: 'Funds released; repayment not yet begun.' },
  ACTIVE: { label: 'Active', tone: 'success', description: 'Repayment is under way and on schedule.' },
  COMPLETED: { label: 'Completed', tone: 'success', description: 'Fully repaid. Guarantors released.' },
  DEFAULTED: {
    label: 'Defaulted',
    tone: 'danger',
    description: 'Sustained arrears. Borrowing eligibility is suspended; no charges are added.',
  },
  WRITTEN_OFF: { label: 'Written off', tone: 'neutral', description: 'No longer pursued.' },
  CANCELLED: { label: 'Cancelled', tone: 'neutral' },
};

export const APPLICATION_STATUS: Record<LoanApplicationStatus, StatusPresentation> = {
  DRAFT: { label: 'Draft', tone: 'neutral', description: 'Not yet submitted.' },
  SUBMITTED: { label: 'Submitted', tone: 'info', description: 'Waiting to be picked up for review.' },
  UNDER_REVIEW: { label: 'Under review', tone: 'info' },
  MORE_INFORMATION_REQUIRED: {
    label: 'More information needed',
    tone: 'warning',
    description: 'The reviewer has asked the member for something further.',
  },
  AWAITING_GUARANTORS: {
    label: 'Awaiting guarantors',
    tone: 'warning',
    description: 'Cannot proceed until every required guarantor has accepted.',
  },
  APPROVED: { label: 'Approved', tone: 'success' },
  REJECTED: { label: 'Rejected', tone: 'danger' },
  WITHDRAWN: { label: 'Withdrawn', tone: 'neutral' },
};

export const INSTALLMENT_STATUS: Record<InstallmentStatus, StatusPresentation> = {
  PENDING: { label: 'Not yet due', tone: 'neutral' },
  PARTIALLY_PAID: { label: 'Part paid', tone: 'warning' },
  PAID: { label: 'Paid', tone: 'success' },
  // Overdue is red, but nothing is being added to the balance — the
  // consequence is eligibility, not money.
  OVERDUE: { label: 'Overdue', tone: 'danger', description: 'Past due. No penalty is charged.' },
  WAIVED: { label: 'Waived', tone: 'neutral' },
};

export const KYC_STATUS: Record<KycStatus, StatusPresentation> = {
  NOT_STARTED: { label: 'Not started', tone: 'neutral' },
  IN_PROGRESS: { label: 'In progress', tone: 'info' },
  SUBMITTED: { label: 'Submitted', tone: 'info' },
  UNDER_REVIEW: { label: 'Under review', tone: 'info' },
  REQUIRES_CORRECTION: { label: 'Needs correction', tone: 'warning' },
  APPROVED: { label: 'Verified', tone: 'success' },
  REJECTED: { label: 'Rejected', tone: 'danger' },
};

export const GUARANTOR_STATUS: Record<GuarantorStatus, StatusPresentation> = {
  PENDING_CONFIRMATION: {
    label: 'Awaiting call',
    tone: 'warning',
    description: 'UNIFEMGA has not yet telephoned them.',
  },
  CONFIRMED: {
    label: 'Confirmed',
    tone: 'success',
    description: 'An officer spoke to them and recorded the call.',
  },
  UNREACHABLE: {
    label: 'Could not reach',
    tone: 'warning',
    description: 'The number did not answer. Try again or ask for another.',
  },
  DECLINED: { label: 'Declined', tone: 'danger' },
  WITHDRAWN: { label: 'Withdrawn', tone: 'neutral' },
  RELEASED: { label: 'Released', tone: 'success', description: 'The loan was settled.' },
};

export const MEMBER_STATUS: Record<MemberStatus, StatusPresentation> = {
  PENDING: { label: 'Pending', tone: 'warning' },
  ACTIVE: { label: 'Active', tone: 'success' },
  INACTIVE: { label: 'Inactive', tone: 'neutral' },
  SUSPENDED: { label: 'Suspended', tone: 'danger' },
  EXITED: { label: 'Exited', tone: 'neutral' },
};

export const DISBURSEMENT_STATUS: Record<DisbursementStatus, StatusPresentation> = {
  PENDING: { label: 'Pending', tone: 'warning' },
  APPROVED: { label: 'Approved', tone: 'info' },
  PROCESSING: { label: 'Processing', tone: 'info' },
  COMPLETED: { label: 'Completed', tone: 'success' },
  FAILED: { label: 'Failed', tone: 'danger' },
  CANCELLED: { label: 'Cancelled', tone: 'neutral' },
};

export const NOTIFICATION_STATUS: Record<NotificationStatus, StatusPresentation> = {
  SCHEDULED: { label: 'Scheduled', tone: 'neutral' },
  QUEUED: { label: 'Queued', tone: 'info' },
  SENT: { label: 'Sent', tone: 'info' },
  DELIVERED: { label: 'Delivered', tone: 'success' },
  FAILED: { label: 'Failed', tone: 'danger' },
  CANCELLED: { label: 'Cancelled', tone: 'neutral' },
};

/** Falls back to a neutral badge rather than throwing on an unrecognised value. */
export function presentStatus(
  map: Record<string, StatusPresentation>,
  status: string | null | undefined,
): StatusPresentation {
  if (!status) return { label: '—', tone: 'neutral' };
  return map[status] ?? { label: humanise(status), tone: 'neutral' };
}

function humanise(value: string): string {
  const text = value.replace(/_/g, ' ').toLowerCase();
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Tone for an arrears age. Used on dashboards where the number, not the
 * status enum, is what the reader is scanning.
 */
export function arrearsTone(daysInArrears: number): StatusTone {
  if (daysInArrears <= 0) return 'success';
  if (daysInArrears <= 7) return 'warning';
  return 'danger';
}

/** A member's claim that they have paid, before an officer checks it. */
export const DECLARATION_STATUS: Record<string, StatusPresentation> = {
  PENDING: { label: 'Under review', tone: 'warning' },
  CONFIRMED: { label: 'Confirmed', tone: 'success' },
  REJECTED: { label: 'Declined', tone: 'danger' },
};
